// Active time on the site and per exercise (owner, 08/10: « stocker le temps passé sur le site et par exercice »).
// The browser counts seconds while the page is visible and the user active, and sends them every HEARTBEAT_SECONDS.
// The server never trusts a client total: each heartbeat adds at most MAX_INCREMENT_SECONDS and never more than the
// wall time since the previous heartbeat on the same record, so several tabs (or a forged client) cannot add up to
// more than real time. Pure (rules.test.ts).

export const HEARTBEAT_SECONDS = 30
export const TICK_SECONDS = 5
export const IDLE_AFTER_SECONDS = 120
export const MAX_INCREMENT_SECONDS = Math.round(HEARTBEAT_SECONDS * 1.5)
export const ACTIVITY_TIME_ZONE = 'Europe/Paris'

export type ActivityTarget =
  | { kind: 'training'; exerciseId: number }
  | { kind: 'exam'; participationId: number; itemIndex: number }
  | { kind: 'course'; courseId: number }
  | { kind: 'live'; code: string }

export type Heartbeat = { seconds: number; target: ActivityTarget | null }

const isId = (v: unknown): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v > 0

// Browser side: seconds to count for the time elapsed since the previous tick. Nothing while the tab is hidden or once
// the user has been idle for IDLE_AFTER_SECONDS; a long gap (laptop asleep, throttled timer) counts as two ticks at most.
export function activeDelta(state: { visible: boolean; now: number; lastTick: number; lastInput: number }): number {
  if (!state.visible || state.now - state.lastInput >= IDLE_AFTER_SECONDS * 1000) return 0
  const delta = (state.now - state.lastTick) / 1000
  return Number.isFinite(delta) ? Math.min(Math.max(delta, 0), TICK_SECONDS * 2) : 0
}

// Server side: seconds actually added for a heartbeat reporting `reported` seconds, given the previous heartbeat on the
// same record (null = first one).
export function boundedIncrement(reported: unknown, lastAt: string | Date | null | undefined, now = new Date()): number {
  const value = typeof reported === 'number' && Number.isFinite(reported) ? Math.floor(reported) : 0
  let seconds = Math.min(Math.max(value, 0), MAX_INCREMENT_SECONDS)
  if (lastAt) {
    const elapsed = Math.floor((now.getTime() - new Date(lastAt).getTime()) / 1000)
    if (Number.isFinite(elapsed)) seconds = Math.min(seconds, Math.max(elapsed, 0))
  }
  return seconds
}

export function parseTarget(value: unknown): ActivityTarget | null {
  if (!value || typeof value !== 'object') return null
  const v = value as Record<string, unknown>
  if (v.kind === 'training' && isId(v.exerciseId)) return { kind: 'training', exerciseId: v.exerciseId }
  if (v.kind === 'exam' && isId(v.participationId) && Number.isSafeInteger(v.itemIndex) && (v.itemIndex as number) >= 0) {
    return { kind: 'exam', participationId: v.participationId, itemIndex: v.itemIndex as number }
  }
  if (v.kind === 'course' && isId(v.courseId)) return { kind: 'course', courseId: v.courseId }
  // Join code: normalised and looked up by the server (lib/live/code).
  if (v.kind === 'live' && typeof v.code === 'string' && v.code.length > 0 && v.code.length <= 32) return { kind: 'live', code: v.code }
  return null
}

export function parseHeartbeat(body: unknown): Heartbeat | null {
  if (!body || typeof body !== 'object') return null
  const { seconds, target } = body as Record<string, unknown>
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return null
  return { seconds: Math.min(Math.floor(seconds), MAX_INCREMENT_SECONDS), target: parseTarget(target) }
}

// Calendar day of the daily totals, in French time (the site's audience): YYYY-MM-DD.
export function activityDay(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: ACTIVITY_TIME_ZONE, year: 'numeric', month: '2-digit', day: '2-digit' }).format(date)
}

// "—" when unknown (no data before 08/10/2026), otherwise "45 s", "12 min", "1 h 05".
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—'
  const s = Math.max(0, Math.round(seconds))
  if (s < 60) return `${s} s`
  const minutes = Math.round(s / 60)
  if (minutes < 60) return `${minutes} min`
  return `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}`
}

// Minutes with one decimal for spreadsheets (null = unknown).
export const toMinutes = (seconds: number | null | undefined) => (seconds === null || seconds === undefined ? null : Math.round(seconds / 6) / 10)
